import React, { useState, useEffect, useRef } from 'react';
import { supabase, insertRow, updateRow } from '../db/engine';
import { useApp } from '../context/AppContext';
import { X, Check, FileText, AlertTriangle, Edit2, Trash2, Printer } from 'lucide-react';
import { fmtFecha, fmtFechaHora } from '../utils/fecha';
import { fmtMoneda } from '../utils/moneda';
import { getAbrev } from '../utils/marca';
import { etiquetaDondePago, imprimirReciboCobro } from '../utils/reciboCobro';
import { reaplicarSacoPoliza } from '../db/saco';
import { generarComisionesDelCobro, revertirComisionesDeCobroAnulado, ajustarComisionesPorCorreccionDeCobro } from '../db/comisiones';
import BeneficiarioConfidencial from './BeneficiarioConfidencial';
import ReciboCobroExito from './ReciboCobroExito';

const fmt$ = fmtMoneda;
const hoy = () => new Date().toISOString().slice(0, 10);
// Último día del mes en curso (YYYY-MM-DD): lo "por cobrar este mes".
const finMes = () => {
  const d = new Date();
  const y = d.getFullYear(), m = d.getMonth();
  const ultimo = new Date(y, m + 1, 0).getDate();
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(ultimo).padStart(2, '0')}`;
};

const EMPTY_COBRO_TOTAL = {
  monto_recibido:'', donde_pago:'Aseguradora', fecha_pago_aseg:'',
  forma_pago_real:'', num_recibo_aseg:'', notas_cobro:'',
};

// ──────────────────────────────────────────────────────────────
// Ventana de "Detalle de Cobros" de una póliza. Se usa desde la
// pestaña Cobros y Pagos (botón Ver) y desde Morosidad (clic en la
// póliza), sin cambiar de pestaña: se abre encima y al cerrar se
// queda donde estaba.
// Props: polizaRef={ poliza_id, poliza_num, cliente }, onClose, onChanged
// ──────────────────────────────────────────────────────────────
export default function DetalleCobrosModal({ polizaRef, onClose, onChanged }) {
  const { canEdit, user } = useApp();
  const abrev = getAbrev();
  const [detalle, setDetalle] = useState(null);        // { poliza, cuotas, cuotasAnteriores, vigencias }
  const [showCobroTotal, setShowCobroTotal] = useState(false);
  const [cobroTotalForm, setCobroTotalForm] = useState(EMPTY_COBRO_TOTAL);
  const [guardandoCobroTotal, setGuardandoCobroTotal] = useState(false);
  const aplicandoCobroRef = useRef(false);
  const [subVista, setSubVista] = useState('cuotas');  // 'cuotas' | 'cobros' | 'poliza'
  const [cobrosRecibidos, setCobrosRecibidos] = useState([]);
  const [bitacoraCobro, setBitacoraCobro] = useState([]);
  const [notaCobro, setNotaCobro] = useState('');
  const [guardandoNota, setGuardandoNota] = useState(false);
  const [pagoDetalle, setPagoDetalle] = useState(null); // { cuota, fila }
  const [reciboExito, setReciboExito] = useState(null); // recibo tras pago en oficina
  const [modalEditCobro, setModalEditCobro] = useState(null);
  const [modalAnularCobro, setModalAnularCobro] = useState(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [editCobroForm, setEditCobroForm] = useState({});
  const [procesandoCobro, setProcesandoCobro] = useState(false);

  const cargarCuotasAnteriores = async (poliza) => {
    if (!poliza?.poliza_anterior_id) return [];
    const { data } = await supabase.from('pagos').select('*')
      .eq('poliza_id', poliza.poliza_anterior_id).order('cuota_num', { ascending: true });
    return data || [];
  };

  const cargar = async (r) => {
    const [{ data: cuotas }, { data: polizaData }] = await Promise.all([
      supabase.from('pagos').select('*').eq('poliza_id', r.poliza_id).order('cuota_num', { ascending: true }),
      supabase.from('polizas')
        .select('*, clientes(nombre), aseguradoras(nombre), ramos(nombre), subramos(nombre), ejecutivos!polizas_ejecutivo_id_fkey(nombre)')
        .eq('id', r.poliza_id)
        .single(),
    ]);
    const poliza = {
      id: r.poliza_id,
      numero: r.poliza_num,
      cliente: r.cliente,
      ...(polizaData || {}),
      ramo:       polizaData?.ramos?.nombre       || '—',
      subramo:    polizaData?.subramos?.nombre    || null,
      aseguradora: polizaData?.aseguradoras?.nombre || '—',
      ejecutivo:  polizaData?.ejecutivos?.nombre  || '—',
    };

    let cuotasAnteriores = [];
    if (polizaData?.poliza_anterior_id) {
      const { data: polAnt } = await supabase.from('polizas')
        .select('id, numero, fecha_inicio, fecha_vencimiento').eq('id', polizaData.poliza_anterior_id).single();
      const { data: pagosAnt } = await supabase.from('pagos').select('*')
        .eq('poliza_id', polizaData.poliza_anterior_id).order('cuota_num', { ascending: true });
      if (polAnt) {
        poliza.poliza_anterior = polAnt;
        cuotasAnteriores = pagosAnt || [];
      }
    }

    // Cadena de vigencias (mismo número + anterior + renovaciones) para el selector.
    let vigencias = [];
    {
      const selVig = 'id, numero, fecha_inicio, fecha_vencimiento, estado, poliza_anterior_id';
      const numero = polizaData?.numero || r.poliza_num;
      const map = new Map();
      if (numero) {
        const { data: mismoNum } = await supabase.from('polizas').select(selVig).eq('numero', numero);
        for (const v of (mismoNum || [])) map.set(v.id, v);
      }
      if (polizaData?.poliza_anterior_id && polizaData.poliza_anterior_id !== r.poliza_id && !map.has(polizaData.poliza_anterior_id)) {
        const { data: ant } = await supabase.from('polizas').select(selVig).eq('id', polizaData.poliza_anterior_id).maybeSingle();
        if (ant) map.set(ant.id, ant);
      }
      const { data: renovs } = await supabase.from('polizas').select(selVig).eq('poliza_anterior_id', r.poliza_id);
      for (const v of (renovs || [])) if (v.id !== r.poliza_id) map.set(v.id, v);
      if (!map.has(r.poliza_id)) map.set(r.poliza_id, { id: r.poliza_id, numero, fecha_inicio: polizaData?.fecha_inicio, fecha_vencimiento: polizaData?.fecha_vencimiento, estado: polizaData?.estado });
      vigencias = [...map.values()].sort((a, b) => (a.fecha_inicio || '').localeCompare(b.fecha_inicio || ''));
    }

    const { data: bitacora } = await supabase
      .from('bitacora_cobros')
      .select('*, usuarios(nombre)')
      .eq('poliza_id', r.poliza_id)
      .order('creado_en', { ascending: false });
    setSubVista('cuotas');
    setNotaCobro('');
    setShowCobroTotal(false);
    setCobrosRecibidos([]);
    setDetalle({ poliza, cuotas: cuotas || [], cuotasAnteriores, vigencias, vigenciaActualId: r.poliza_id });
    setBitacoraCobro(bitacora || []);
    const { data: cobros } = await supabase.from('cobros_recibidos')
      .select('*, cobro_cuotas(*, pagos(id, cuota_num, monto, estado, monto_pagado))')
      .eq('poliza_id', r.poliza_id)
      .order('fecha', { ascending: true });
    setCobrosRecibidos(cobros || []);
  };

  // Cargar al abrir / cambiar de póliza objetivo
  useEffect(() => {
    if (polizaRef?.poliza_id) cargar(polizaRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polizaRef]);

  const agregarNotaCobro = async () => {
    if (!notaCobro.trim() || !detalle) return;
    setGuardandoNota(true);
    try {
      await insertRow('bitacora_cobros', { poliza_id: detalle.poliza.id, usuario_id: user?.id, nota: notaCobro.trim() });
      const { data } = await supabase.from('bitacora_cobros').select('*, usuarios(nombre)').eq('poliza_id', detalle.poliza.id).order('creado_en', { ascending: false });
      setBitacoraCobro(data || []);
      setNotaCobro('');
    } catch (e) { alert('Error: ' + e.message); }
    finally { setGuardandoNota(false); }
  };

  // Distribuye monto total recibido a cuotas — maneja parciales — genera UNA comisión
  const handleCobroTotal = async () => {
    if (aplicandoCobroRef.current) return;   // anti doble-clic (carrera)
    aplicandoCobroRef.current = true;
    try {
    const montoTotal = parseFloat(cobroTotalForm.monto_recibido) || 0;
    if (!montoTotal || montoTotal <= 0) { alert('Ingresa el monto recibido.'); return; }
    if (!cobroTotalForm.fecha_pago_aseg) { alert('La fecha de pago es requerida.'); return; }

    // Aviso de recibo duplicado (solo pagos en la aseguradora — en oficina el recibo lo genera el sistema).
    if (cobroTotalForm.donde_pago !== 'RU' && cobroTotalForm.num_recibo_aseg && String(cobroTotalForm.num_recibo_aseg).trim()) {
      const { data: dupRec } = await supabase.from('cobros_recibidos')
        .select('id, monto, fecha, anulado')
        .eq('poliza_id', detalle.poliza.id)
        .eq('num_recibo_aseg', String(cobroTotalForm.num_recibo_aseg).trim());
      const yaExiste = (dupRec || []).find(c => !c.anulado);
      if (yaExiste) {
        const ok = window.confirm(
          `⚠️ Ya existe un cobro con el recibo de aseguradora "${cobroTotalForm.num_recibo_aseg}" en esta póliza ` +
          `(${yaExiste.fecha} · ${fmt$(yaExiste.monto)}).\n\n¿Estás registrando un PAGO DUPLICADO?\n\n` +
          `Aceptar = registrarlo igual · Cancelar = no duplicar.`
        );
        if (!ok) return;   // el finally libera el lock
      }
    }

    // Mismo saco que el preview: la cuota más antigua primero, cruzando a la
    // vigencia anterior. reaplicarSacoPoliza lo recalcula igual justo después,
    // pero así lo que se escribe ya nace bien y coincide con lo que se prometió.
    const cuotasPend = [...(detalle.cuotasAnteriores || []), ...(detalle.cuotas || [])]
      .filter(c => ['pendiente','parcial'].includes(c.estado))
      .sort((a, b) => (a.fecha_vencimiento || '').localeCompare(b.fecha_vencimiento || '')
                   || (a.cuota_num || 0) - (b.cuota_num || 0));

    if (!cuotasPend.length) {
      const ok = window.confirm('Esta póliza está en saldo $0.00.\n\nEl cobro se registrará como CRÉDITO (sin aplicarse a una cuota). ¿Continuar?');
      if (!ok) { aplicandoCobroRef.current = false; return; }
    }

    const { data: agentesPoliza } = await supabase.from('poliza_agentes').select('id').eq('poliza_id', detalle.poliza.id).limit(1);
    if (!agentesPoliza?.length) {
      const ok = window.confirm(`⚠️ Esta póliza no tiene agente.\nEl 100% de la comisión quedará para ${abrev}.\n¿Continuar?`);
      if (!ok) return;
    }

    // Distribuir monto a cuotas (respetando monto_pagado en parciales)
    let restante = montoTotal;
    const aplicaciones = []; // { cuota, montoAplicado, nuevoPagado, completa }
    for (const c of cuotasPend) {
      if (restante <= 0.001) break;
      const montoCuota   = parseFloat(c.monto) || 0;
      const yaPagado     = parseFloat(c.monto_pagado) || 0;
      const faltante     = Math.round((montoCuota - yaPagado) * 100) / 100;
      if (faltante <= 0) continue;

      if (restante >= faltante - 0.001) {
        aplicaciones.push({ cuota: c, montoAplicado: faltante, nuevoPagado: montoCuota, completa: true });
        restante = Math.round((restante - faltante) * 100) / 100;
      } else {
        aplicaciones.push({ cuota: c, montoAplicado: restante, nuevoPagado: yaPagado + restante, completa: false });
        restante = 0;
      }
    }

    const montoEfectivo = montoTotal;
    const pagoEnOficina = cobroTotalForm.donde_pago === 'RU';
    setGuardandoCobroTotal(true);
    try {
      // N° de recibo: en OFICINA lo genera el sistema (RU-AAAA-NNNNN) al confirmar.
      let numReciboRU = null;
      if (pagoEnOficina) {
        const { data: numSec, error: errSec } = await supabase.rpc('next_recibo_ru');
        if (errSec || !numSec) throw new Error('No se pudo generar el número de recibo de oficina. ' + (errSec?.message || ''));
        numReciboRU = numSec;
      }
      const numReciboAseg = pagoEnOficina ? null : (cobroTotalForm.num_recibo_aseg || null);
      const cobro = await insertRow('cobros_recibidos', {
        poliza_id:      detalle.poliza.id,
        monto:          montoEfectivo,
        fecha:          cobroTotalForm.fecha_pago_aseg,
        donde_pago:     cobroTotalForm.donde_pago || null,
        forma_pago:     cobroTotalForm.forma_pago_real || null,
        num_recibo_aseg: numReciboAseg,
        num_recibo_ru:  numReciboRU,
        notas:          cobroTotalForm.notas_cobro || null,
      });

      for (const ap of aplicaciones) {
        await insertRow('cobro_cuotas', {
          cobro_id: cobro.id, pago_id: ap.cuota.id, monto_aplicado: ap.montoAplicado,
        });
        await updateRow('pagos', ap.cuota.id, {
          estado:       ap.completa ? 'pagado' : 'parcial',
          monto_pagado: Math.round(ap.nuevoPagado * 100) / 100,
          fecha_pago:   ap.completa ? cobroTotalForm.fecha_pago_aseg : (ap.cuota.fecha_pago || null),
          donde_pago:   ap.completa ? (cobroTotalForm.donde_pago || null) : ap.cuota.donde_pago,
          forma_pago_real: ap.completa ? (cobroTotalForm.forma_pago_real || null) : ap.cuota.forma_pago_real,
          num_recibo_aseg: ap.completa ? numReciboAseg : ap.cuota.num_recibo_aseg,
          num_recibo_ru:   ap.completa ? numReciboRU : null,
        });
      }

      // El saco primero: reparte el cobro entre las cuotas y puede cruzar a la vigencia
      // anterior. La comisión se calcula después, sobre ese reparto real.
      await reaplicarSacoPoliza(detalle.poliza.id);
      await generarComisionesDelCobro(cobro.id, detalle.poliza.id, montoEfectivo);

      const [{ data: cuotas }, { data: cobros }] = await Promise.all([
        supabase.from('pagos').select('*').eq('poliza_id', detalle.poliza.id).order('cuota_num'),
        supabase.from('cobros_recibidos').select('*, cobro_cuotas(*, pagos(id, cuota_num, monto, estado, monto_pagado))').eq('poliza_id', detalle.poliza.id).order('fecha', { ascending: true }),
      ]);
      const cuotasAntCT = await cargarCuotasAnteriores(detalle.poliza);
      setDetalle(d => ({ ...d, cuotas: cuotas || [], cuotasAnteriores: cuotasAntCT }));
      setCobrosRecibidos(cobros || []);
      setShowCobroTotal(false);
      if (pagoEnOficina) {
        setReciboExito({
          numRecibo: numReciboRU,
          fecha: cobroTotalForm.fecha_pago_aseg,
          monto: montoEfectivo,
          formaPago: cobroTotalForm.forma_pago_real || null,
          cliente: detalle.poliza.cliente,
          polizaNum: detalle.poliza.numero,
          aseguradora: detalle.poliza.aseguradora,
          ramo: detalle.poliza.ramo,
          cuotas: aplicaciones.map(ap => ({ num: ap.cuota.cuota_num, monto: ap.montoAplicado, parcial: !ap.completa })),
          notas: cobroTotalForm.notas_cobro || null,
          registradoPor: user?.nombre || user?.email || null,
        });
      }
      setCobroTotalForm(EMPTY_COBRO_TOTAL);
      await onChanged?.();
    } catch(e) { alert('Error: ' + (e.message || '')); }
    finally { setGuardandoCobroTotal(false); }
    } finally { aplicandoCobroRef.current = false; }
  };

  const handleEditarCobro = async () => {
    if (!modalEditCobro) return;
    // Corrección del MONTO (se registraron 105 y eran 95): mueve cuotas y comisión, así
    // que exige motivo y deja rastro. Un cobro ya remesado no se corrige aquí: la remesa
    // guardó su monto y dejaría de cuadrar con lo que se le envió a la aseguradora.
    const montoAntes = Math.round((parseFloat(modalEditCobro.monto) || 0) * 100) / 100;
    const montoNuevo = Math.round((parseFloat(editCobroForm.monto) || 0) * 100) / 100;
    const cambiaMonto = Math.abs(montoNuevo - montoAntes) > 0.005;
    if (!(montoNuevo > 0)) { alert('El monto del cobro debe ser mayor que cero. Si el cobro no existió, anúlalo.'); return; }
    if (cambiaMonto && modalEditCobro.remesa_id) {
      alert('Este cobro ya va dentro de una remesa a la aseguradora: su monto no se puede corregir aquí porque la remesa dejaría de cuadrar.');
      return;
    }
    if (cambiaMonto && !(editCobroForm.motivo_monto || '').trim()) { alert('Escribe el motivo de la corrección del monto.'); return; }
    setProcesandoCobro(true);
    try {
      const antiguoDonde = modalEditCobro.donde_pago;
      const nuevoDonde   = editCobroForm.donde_pago;
      const quien = user?.nombre || user?.email || 'Sistema';
      const motivoMonto = (editCobroForm.motivo_monto || '').trim();
      const trazaMonto = cambiaMonto
        ? `✏️ Monto corregido de ${fmt$(montoAntes)} a ${fmt$(montoNuevo)} por ${quien} el ${fmtFecha(new Date().toISOString().slice(0, 10))} — ${motivoMonto}`
        : '';

      await updateRow('cobros_recibidos', modalEditCobro.id, {
        fecha:           editCobroForm.fecha,
        donde_pago:      editCobroForm.donde_pago || null,
        forma_pago:      editCobroForm.forma_pago || null,
        num_recibo_aseg: editCobroForm.num_recibo_aseg || null,
        notas:           [editCobroForm.notas, trazaMonto].filter(Boolean).join('\n') || null,
        ...(cambiaMonto ? { monto: montoNuevo } : {}),
      });

      for (const cc of (modalEditCobro.cobro_cuotas || [])) {
        if (cc.pagos?.estado === 'pagado' || cc.pagos?.estado === 'parcial') {
          await updateRow('pagos', cc.pagos.id, {
            fecha_pago:      editCobroForm.fecha,
            donde_pago:      editCobroForm.donde_pago || null,
            forma_pago_real: editCobroForm.forma_pago || null,
            num_recibo_aseg: editCobroForm.num_recibo_aseg || null,
          });
        }
      }

      if (antiguoDonde !== nuevoDonde) {
        const { data: coms } = await supabase.from('comisiones')
          .select('id, estado').eq('cobro_id', modalEditCobro.id);
        for (const com of (coms || [])) {
          if (antiguoDonde === 'Aseguradora' && nuevoDonde === 'RU') {
            if (com.estado === 'pendiente_aseg')
              await updateRow('comisiones', com.id, { estado: 'pendiente' });
          } else if (antiguoDonde === 'RU' && nuevoDonde === 'Aseguradora') {
            if (com.estado === 'pendiente')
              await updateRow('comisiones', com.id, { estado: 'pendiente_aseg' });
          }
        }
      }

      await reaplicarSacoPoliza(modalEditCobro.poliza_id || detalle.poliza.id);

      // Con el saco ya reaplicado, la comisión sigue al monto corregido: lo que nadie ha
      // pagado se regenera; lo que ya avanzó recibe un ajuste que dice de cuánto a cuánto.
      let ajustes = [];
      if (cambiaMonto) {
        ajustes = await ajustarComisionesPorCorreccionDeCobro(
          { ...modalEditCobro, monto: montoNuevo, fecha: editCobroForm.fecha, num_recibo_aseg: editCobroForm.num_recibo_aseg || null },
          { montoAntes, motivo: motivoMonto, usuario: quien });
        await insertRow('bitacora_cobros', {
          poliza_id: modalEditCobro.poliza_id || detalle.poliza.id, usuario_id: user?.id,
          nota: `${trazaMonto} (cobro ${modalEditCobro.num_recibo_ru || modalEditCobro.num_recibo_aseg || '#' + modalEditCobro.id})`,
        }).catch(() => null);
        const { data: bit } = await supabase.from('bitacora_cobros').select('*, usuarios(nombre)')
          .eq('poliza_id', detalle.poliza.id).order('creado_en', { ascending: false });
        setBitacoraCobro(bit || []);
      }

      setModalEditCobro(null);
      if (ajustes.length) {
        alert(`Monto corregido. La comisión de este cobro ya estaba en proceso o pagada: no se reescribió. `
          + `Se generó ${ajustes.length} ajuste(s) por la diferencia, que entran a la próxima liquidación.`);
      }
      const { data: cobros } = await supabase.from('cobros_recibidos')
        .select('*, cobro_cuotas(*, pagos(id, cuota_num, monto, estado, monto_pagado))')
        .eq('poliza_id', detalle.poliza.id).order('fecha', { ascending: true });
      setCobrosRecibidos(cobros || []);
      const { data: cuotas } = await supabase.from('pagos').select('*').eq('poliza_id', detalle.poliza.id).order('cuota_num');
      const cuotasAntEdit = await cargarCuotasAnteriores(detalle.poliza);
      setDetalle(d => ({ ...d, cuotas: cuotas || [], cuotasAnteriores: cuotasAntEdit }));
      await onChanged?.();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setProcesandoCobro(false); }
  };

  const handleAnularCobro = async () => {
    if (!modalAnularCobro || !motivoAnulacion.trim()) return;
    setProcesandoCobro(true);
    try {
      await updateRow('cobros_recibidos', modalAnularCobro.id, {
        anulado: true,
        motivo_anulacion: motivoAnulacion.trim(),
        anulado_en: new Date().toISOString(),
        anulado_por: user?.nombre || user?.email || 'Sistema',
      });
      // Las comisiones que nadie ha tocado se borran; las que ya avanzaron (la aseguradora
      // pagó, se aprobó o se le liquidó al agente) se QUEDAN y se netean con un negativo
      // que dice de qué cobro es. Va ANTES del saco: al reaplicar, los demás cobros pueden
      // cambiar de vigencia y sus comisiones se ajustan solas.
      const rev = await revertirComisionesDeCobroAnulado(modalAnularCobro, {
        motivo: motivoAnulacion.trim(),
        usuario: user?.nombre || user?.email || 'Sistema',
      });
      await reaplicarSacoPoliza(modalAnularCobro.poliza_id || detalle.poliza.id);
      setModalAnularCobro(null); setMotivoAnulacion('');
      if (rev.revertidas) {
        alert(`Cobro anulado. ${rev.revertidas} comisión(es) de este cobro ya estaban en proceso o pagadas: `
          + 'no se borraron. Se generó un ajuste en negativo por cada una, en "Pendiente Agente", '
          + 'para que se descuente en la próxima liquidación.');
      }
      const { data: cobros } = await supabase.from('cobros_recibidos')
        .select('*, cobro_cuotas(*, pagos(id, cuota_num, monto, estado, monto_pagado))')
        .eq('poliza_id', detalle.poliza.id).order('fecha', { ascending: true });
      setCobrosRecibidos(cobros || []);
      const { data: cuotas } = await supabase.from('pagos').select('*').eq('poliza_id', detalle.poliza.id).order('cuota_num');
      const cuotasAntAnul = await cargarCuotasAnteriores(detalle.poliza);
      setDetalle(d => ({ ...d, cuotas: cuotas || [], cuotasAnteriores: cuotasAntAnul }));
      await onChanged?.();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setProcesandoCobro(false); }
  };

  return (
    <>
      {/* Modal: Detalle de póliza — cuotas + bitácora + aplicar pago */}
      {detalle && (() => {
        const { poliza, cuotas, cuotasAnteriores } = detalle;
        const hoyStr = hoy();
        const totalPagado   = cuotas.filter(c=>c.estado==='pagado').reduce((s,c)=>s+(parseFloat(c.monto)||0),0)
          + cuotas.filter(c=>c.estado==='parcial').reduce((s,c)=>s+(parseFloat(c.monto_pagado)||0),0);
        const totalPendiente = cuotas.filter(c=>c.estado==='pendiente').reduce((s,c)=>s+(parseFloat(c.monto)||0),0)
          + cuotas.filter(c=>c.estado==='parcial').reduce((s,c)=>s+(parseFloat(c.monto)-(parseFloat(c.monto_pagado)||0)),0);
        const saldoAnterior = (cuotasAnteriores||[]).filter(c=>['pendiente','parcial'].includes(c.estado))
          .reduce((s,c)=>s+(parseFloat(c.monto)-(parseFloat(c.monto_pagado)||0)),0);
        const saldoTotal = totalPendiente + saldoAnterior;
        return (
          <div className="modal-overlay" onClick={onClose}>
            <div className="modal modal-lg" style={{ maxWidth:820 }} onClick={e=>e.stopPropagation()}>
              <div className="modal-header">
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  {subVista === 'poliza' && (
                    <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setSubVista('cuotas')} title="Volver a cuotas">
                      ←
                    </button>
                  )}
                  <div>
                    <div className="modal-title">{subVista === 'poliza' ? 'Detalle de Póliza' : 'Detalle de Cobros'} — {poliza.numero}</div>
                    <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>{poliza.cliente} · {poliza.ramo}{poliza.subramo ? ` / ${poliza.subramo}` : ''}</div>
                  </div>
                </div>
                <button className="btn btn-icon btn-secondary btn-sm" onClick={onClose}><X size={16}/></button>
              </div>

              <div className="modal-body" style={{ maxHeight:'78vh', overflowY:'auto' }}>

                {/* ── Sub-vista: Detalle completo de la póliza ── */}
                {subVista === 'poliza' && (
                  <div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:20 }}>
                      {[
                        ['Aseguradora', poliza.aseguradora],
                        ['Ramo', poliza.ramo + (poliza.subramo ? ` / ${poliza.subramo}` : '')],
                        ['Estado', poliza.estado],
                        ['Prima Neta', poliza.prima ? fmt$(poliza.prima) : '—'],
                        ['Impuesto', poliza.impuesto != null ? `${poliza.impuesto}%` : '—'],
                        ['Comisión Ramo', poliza.comision_ramo != null ? `${poliza.comision_ramo}%` : '—'],
                        ['Emisión p/ aseguradora', poliza.tipo_emision || '—'],
                        ['Forma de Pago', poliza.forma_pago || '—'],
                        ['No. Cuotas', poliza.num_cuotas || '—'],
                        ['Inicio Vigencia', fmtFecha(poliza.fecha_inicio) || '—'],
                        ['Vencimiento', fmtFecha(poliza.fecha_vencimiento) || '—'],
                        ['Ejecutivo', poliza.ejecutivo || '—'],
                      ].map(([l, v]) => (
                        <div key={l} style={{ padding:'10px 14px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8 }}>
                          <div style={{ fontSize:10, color:'var(--silver)', textTransform:'uppercase', marginBottom:4 }}>{l}</div>
                          <div style={{ fontSize:14, fontWeight:500 }}>{v || '—'}</div>
                        </div>
                      ))}
                    </div>
                    {poliza.descripcion_riesgo && (
                      <div style={{ padding:'10px 14px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8, marginBottom:12 }}>
                        <div style={{ fontSize:10, color:'var(--silver)', textTransform:'uppercase', marginBottom:4 }}>Descripción del Riesgo</div>
                        <div style={{ fontSize:13 }}>{poliza.descripcion_riesgo}</div>
                      </div>
                    )}
                    {poliza.beneficiario_nombre && (
                      <div style={{ padding:'10px 14px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8, marginBottom:12 }}>
                        <div style={{ fontSize:10, color:'var(--silver)', textTransform:'uppercase', marginBottom:4 }}>Beneficiario</div>
                        <BeneficiarioConfidencial
                          nombre={`${poliza.beneficiario_nombre}${poliza.beneficiario_cedula ? ` · ${poliza.beneficiario_cedula}` : ''}`}
                          actualizadoEl={poliza.beneficiarios_actualizado_el}
                          nameStyle={{ fontSize:13 }} subStyle={{ fontSize:11, color:'var(--silver)' }} />
                      </div>
                    )}
                    {poliza.notas && (
                      <div style={{ padding:'10px 14px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8 }}>
                        <div style={{ fontSize:10, color:'var(--silver)', textTransform:'uppercase', marginBottom:4 }}>Notas</div>
                        <div style={{ fontSize:13 }}>{poliza.notas}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Sub-vista: Cuotas y cobros ── */}
                {(subVista === 'cuotas' || subVista === 'cobros') && (() => {
                  const tieneAnterior = cuotasAnteriores?.length > 0;
                  const hayAnteriorPendiente = tieneAnterior && saldoAnterior > 0.01;
                  return (<>

                {/* Selector de vigencia */}
                {detalle.vigencias?.length > 1 && (
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, padding:'8px 12px', background:'rgba(var(--sky-rgb),0.08)', borderRadius:8, border:'1px solid rgba(var(--sky-rgb),0.25)', flexWrap:'wrap' }}>
                    <span style={{ fontSize:11, color:'var(--silver)', textTransform:'uppercase', fontWeight:700, letterSpacing:1 }}>Vigencia</span>
                    <select className="form-select" style={{ maxWidth:360, padding:'4px 10px', fontSize:13, width:'auto' }}
                      value={poliza.id}
                      onChange={e => cargar({ poliza_id: parseInt(e.target.value), poliza_num: poliza.numero, cliente: poliza.cliente })}>
                      {detalle.vigencias.map(v => (
                        <option key={v.id} value={v.id}>
                          {(fmtFecha(v.fecha_inicio) || '?')} → {(fmtFecha(v.fecha_vencimiento) || '?')} · {v.numero}{v.estado ? ` (${v.estado})` : ''}
                        </option>
                      ))}
                    </select>
                    <span style={{ fontSize:11, color:'var(--silver)' }}>{detalle.vigencias.length} vigencias de esta póliza</span>
                  </div>
                )}

                {/* Info rápida de la póliza */}
                <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'stretch' }}>
                  {[
                    ['Aseguradora', poliza.aseguradora],
                    ['Ramo', poliza.ramo + (poliza.subramo ? ` / ${poliza.subramo}` : '')],
                    ['Prima anual', poliza.prima ? fmt$(poliza.prima) : '—'],
                    ['Vigencia', poliza.fecha_inicio && poliza.fecha_vencimiento ? `${fmtFecha(poliza.fecha_inicio)} → ${fmtFecha(poliza.fecha_vencimiento)}` : (fmtFecha(poliza.fecha_inicio) || fmtFecha(poliza.fecha_vencimiento) || '—')],
                    ['Forma de pago', poliza.forma_pago || '—'],
                  ].map(([l, v]) => (
                    <div key={l} style={{ padding:'8px 12px', background:'rgba(var(--steel-rgb),0.35)', borderRadius:8, minWidth:120 }}>
                      <div style={{ fontSize:10, color:'var(--silver)', textTransform:'uppercase', marginBottom:2 }}>{l}</div>
                      <div style={{ fontSize:13, fontWeight:600 }}>{v}</div>
                    </div>
                  ))}
                  <button className="btn btn-secondary btn-sm" style={{ alignSelf:'center', marginLeft:'auto' }}
                    onClick={()=>setSubVista('poliza')}>
                    <FileText size={13}/> Ver póliza completa
                  </button>
                </div>

                {/* Resumen de saldos */}
                {(() => {
                  const saldoAFecha = (detalle.cuotas || [])
                    .filter(c => ['pendiente','parcial'].includes(c.estado) && c.fecha_vencimiento && c.fecha_vencimiento <= finMes())
                    .reduce((s, c) => Math.round((s + (parseFloat(c.monto)||0) - (parseFloat(c.monto_pagado)||0)) * 100) / 100, 0);
                  return (
                <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:140, padding:'10px 14px', background:'rgba(34, 197, 94, 0.08)', borderRadius:8, border:'1px solid rgba(34, 197, 94, 0.2)' }}>
                    <div style={{ fontSize:11, color:'var(--silver)' }}>PAGADO</div>
                    <div style={{ fontSize:18, fontWeight:700, color:'var(--text-success)' }}>{fmt$(totalPagado)}</div>
                  </div>
                  <div style={{ flex:1, minWidth:140, padding:'10px 14px', background: saldoAFecha > 0 ? 'rgba(239, 68, 68, 0.08)' : 'rgba(34, 197, 94, 0.04)', borderRadius:8, border: saldoAFecha > 0 ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(34, 197, 94, 0.15)' }}>
                    <div style={{ fontSize:11, color:'var(--silver)' }}>SALDO DEL MES</div>
                    <div style={{ fontSize:18, fontWeight:700, color: saldoAFecha > 0 ? '#ef4444' : '#22c55e' }}>{fmt$(saldoAFecha)}</div>
                  </div>
                  <div style={{ flex:1, minWidth:140, padding:'10px 14px', background: saldoTotal > 0 ? 'rgba(245, 158, 11, 0.08)' : 'rgba(34, 197, 94, 0.04)', borderRadius:8, border: saldoTotal > 0 ? '1px solid rgba(245, 158, 11, 0.2)' : '1px solid rgba(34, 197, 94, 0.15)' }}>
                    <div style={{ fontSize:11, color:'var(--silver)' }}>SALDO TOTAL</div>
                    <div style={{ fontSize:18, fontWeight:700, color: saldoTotal > 0 ? '#f59e0b' : '#22c55e' }}>{fmt$(saldoTotal)}</div>
                  </div>
                  {saldoAnterior > 0 && (
                    <div style={{ flex:1, minWidth:140, padding:'10px 14px', background:'rgba(239, 68, 68, 0.08)', borderRadius:8, border:'1px solid rgba(239, 68, 68, 0.2)' }}>
                      <div style={{ fontSize:11, color:'var(--silver)' }}>SALDO VIG. ANTERIOR</div>
                      <div style={{ fontSize:18, fontWeight:700, color:'var(--text-danger)' }}>{fmt$(saldoAnterior)}</div>
                    </div>
                  )}
                </div>
                  );
                })()}

                {/* ── Registrar Cobro (el pago se aplica como saco) ── */}
                {canEdit('cobros') && (
                  <div style={{ marginBottom:16 }}>
                    {!showCobroTotal && (
                      <div style={{ display:'flex', gap:8 }}>
                        <button className="btn btn-primary btn-sm" onClick={()=>{ setShowCobroTotal(true); setCobroTotalForm({...EMPTY_COBRO_TOTAL, fecha_pago_aseg:hoy()}); }}>
                          <Check size={14}/> Registrar Cobro{saldoTotal <= 0 ? ' (crédito)' : ''}
                        </button>
                      </div>
                    )}

                    {showCobroTotal && (() => {
                      const montoEnt = parseFloat(cobroTotalForm.monto_recibido) || 0;
                      // Saco: la cuota más ANTIGUA primero, cruzando a la vigencia
                      // anterior si arrastra saldo. Ordenar por cuota_num y mirar solo
                      // la vigencia actual hacía que el mensaje prometiera una cosa y
                      // el saco (reaplicarSacoPoliza) hiciera otra.
                      const pendientes = [...(detalle.cuotasAnteriores||[]), ...(detalle.cuotas||[])]
                        .filter(c=>['pendiente','parcial'].includes(c.estado))
                        .sort((a,b)=>(a.fecha_vencimiento||'').localeCompare(b.fecha_vencimiento||'')
                                  || (a.cuota_num||0)-(b.cuota_num||0));
                      let rest = montoEnt;
                      const preview = [];
                      for (const c of pendientes) {
                        if (rest <= 0.001) break;
                        const faltante = Math.round(((parseFloat(c.monto)||0)-(parseFloat(c.monto_pagado)||0))*100)/100;
                        if (faltante <= 0) continue;
                        if (rest >= faltante - 0.001) {
                          preview.push({ cuota:c, montoAplicado:faltante, completa:true });
                          rest = Math.round((rest-faltante)*100)/100;
                        } else {
                          preview.push({ cuota:c, montoAplicado:rest, completa:false });
                          rest = 0;
                        }
                      }
                      const sobrante = Math.max(0, rest);
                      return (
                      <div style={{ padding:'16px 20px', background:'rgba(34, 197, 94, 0.05)', borderRadius:10, border:'1px solid rgba(34, 197, 94, 0.2)' }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'var(--text-success)', marginBottom:14 }}>💰 Registrar Cobro</div>
                        <div style={{ display:'grid', gridTemplateColumns:'140px 1fr 1fr 1fr 1fr', gap:12, alignItems:'flex-end' }}>
                          <div className="form-group" style={{margin:0}}>
                            <label className="form-label">Monto recibido *</label>
                            <input className="form-input" type="number" step="0.01" placeholder="0.00"
                              value={cobroTotalForm.monto_recibido}
                              onChange={e=>setCobroTotalForm(p=>({...p,monto_recibido:e.target.value}))}
                              style={{borderColor:'rgba(34, 197, 94, 0.4)'}}/>
                          </div>
                          <div className="form-group" style={{margin:0}}>
                            <label className="form-label">¿Dónde pagó?</label>
                            <select className="form-select" value={cobroTotalForm.donde_pago} onChange={e=>setCobroTotalForm(p=>({...p,donde_pago:e.target.value}))}>
                              <option value="Aseguradora">Aseguradora</option><option value="RU">Oficina</option>
                            </select>
                          </div>
                          <div className="form-group" style={{margin:0}}>
                            <label className="form-label">Fecha de pago *</label>
                            <input className="form-input" type="date" value={cobroTotalForm.fecha_pago_aseg} onChange={e=>setCobroTotalForm(p=>({...p,fecha_pago_aseg:e.target.value}))}/>
                          </div>
                          <div className="form-group" style={{margin:0}}>
                            <label className="form-label">Forma de pago</label>
                            <select className="form-select" value={cobroTotalForm.forma_pago_real} onChange={e=>setCobroTotalForm(p=>({...p,forma_pago_real:e.target.value}))}>
                              <option value="">Seleccionar...</option><option value="TC">TC</option><option value="ACH">ACH</option><option value="Yappy">Yappy</option><option value="Efectivo">Efectivo</option><option value="Cheque">Cheque</option>
                            </select>
                          </div>
                          {cobroTotalForm.donde_pago === 'RU' ? (
                            <div className="form-group" style={{margin:0}}>
                              <label className="form-label">No. de recibo</label>
                              <div style={{fontSize:11, color:'var(--sky-light)', padding:'9px 10px', background:'rgba(var(--sky-rgb),0.08)', border:'1px solid rgba(var(--sky-rgb),0.25)', borderRadius:8}}>
                                Se asigna automático al confirmar
                              </div>
                            </div>
                          ) : (
                            <div className="form-group" style={{margin:0}}>
                              <label className="form-label">No. Recibo Aseg.</label>
                              <input className="form-input" value={cobroTotalForm.num_recibo_aseg} onChange={e=>setCobroTotalForm(p=>({...p,num_recibo_aseg:e.target.value}))} placeholder="El que dio la aseguradora"/>
                            </div>
                          )}
                        </div>
                        <div className="form-group" style={{marginTop:10}}>
                          <label className="form-label">Nota</label>
                          <textarea className="form-textarea" rows={2} value={cobroTotalForm.notas_cobro} onChange={e=>setCobroTotalForm(p=>({...p,notas_cobro:e.target.value}))} placeholder="Observaciones..."/>
                        </div>

                        {montoEnt > 0 && (
                          <div style={{ marginTop:12, padding:'10px 14px', background:'rgba(var(--navy-rgb),0.4)', borderRadius:8, fontSize:12 }}>
                            {preview.length > 0 ? (<>
                              <div style={{ color:'var(--text-success)', fontWeight:600, marginBottom:6 }}>
                                {preview.map((p,i)=>(
                                  <span key={i}>
                                    {i>0 && ' + '}
                                    Cuota #{p.cuota.cuota_num} {fmt$(p.montoAplicado)}
                                    {p.cuota.poliza_id !== detalle.vigenciaActualId && (
                                      <span style={{ color:'var(--text-warning)', fontSize:11 }}> (vig. anterior)</span>
                                    )}
                                    {!p.completa && <span style={{color:'var(--text-warning)'}}> (parcial)</span>}
                                    {p.cuota.estado==='parcial' && p.completa && <span style={{color:'var(--sky-light)'}}> (completa)</span>}
                                  </span>
                                ))}
                              </div>
                              <div style={{ color:'var(--silver)' }}>
                                Comisión sobre: <strong style={{color:'var(--sky-light)'}}>{fmt$(montoEnt-sobrante)}</strong>
                                {sobrante > 0.01 && <span style={{color:'var(--text-warning)', marginLeft:8}}>⚠ Sobrante: {fmt$(sobrante)}</span>}
                              </div>
                            </>) : (
                              <div style={{color:'var(--text-purple)'}}>
                                ⏳ Póliza en saldo $0.00 — el cobro de <strong>{fmt$(montoEnt)}</strong> quedará como <strong>crédito</strong> (excedente), sin aplicarse a una cuota.
                              </div>
                            )}
                          </div>
                        )}

                        <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:14 }}>
                          <button className="btn btn-secondary btn-sm" onClick={()=>{ setShowCobroTotal(false); setCobroTotalForm(EMPTY_COBRO_TOTAL); }}>Cancelar</button>
                          <button className="btn btn-primary btn-sm" onClick={handleCobroTotal} disabled={guardandoCobroTotal || montoEnt<=0}>
                            <Check size={13}/> {guardandoCobroTotal ? 'Guardando...' : `Confirmar ${montoEnt>0?fmt$(montoEnt):''}${preview.length===0 && montoEnt>0 ? ' (crédito)' : ''}`}
                          </button>
                        </div>
                      </div>
                      );
                    })()}
                  </div>
                )}

                {/* Tabs Por Cuotas / Por Cobros */}
                <div style={{ display:'flex', gap:2, marginBottom:16, borderBottom:'1px solid rgba(var(--silver-rgb),0.1)' }}>
                  {[['cuotas','Por Cuotas'],['cobros',`Por Cobros (${cobrosRecibidos.length})`]].map(([k,l])=>(
                    <button key={k} onClick={()=>setSubVista(k)} style={{
                      padding:'8px 16px', border:'none', cursor:'pointer', background:'none',
                      fontFamily:'Outfit,sans-serif', fontSize:13, fontWeight:600,
                      color: subVista===k ? 'var(--sky-light)' : 'var(--silver)',
                      borderBottom: subVista===k ? '2px solid var(--sky)' : '2px solid transparent',
                      marginBottom:-1,
                    }}>{l}</button>
                  ))}
                </div>

                {/* Vista: Por Cobros recibidos */}
                {subVista === 'cobros' && (
                  <div>
                    {cobrosRecibidos.length === 0 ? (
                      <div style={{textAlign:'center',padding:'32px 0',color:'var(--silver)',fontSize:13}}>
                        Sin cobros registrados aún — usa "Registrar Cobro" para el primer pago.
                      </div>
                    ) : (
                      <div style={{display:'flex',flexDirection:'column',gap:10}}>
                        {cobrosRecibidos.map((cobro,i)=>(
                          <div key={i} style={{background: cobro.anulado ? 'rgba(239, 68, 68, 0.04)' : 'rgba(var(--navy-rgb),0.4)', borderRadius:10, border: cobro.anulado ? '1px solid rgba(239, 68, 68, 0.25)' : '1px solid rgba(var(--sky-rgb),0.15)', overflow:'hidden', opacity: cobro.anulado ? 0.75 : 1}}>
                            <div style={{display:'flex',alignItems:'center',gap:16,padding:'12px 16px',background: cobro.anulado ? 'rgba(239, 68, 68, 0.06)' : 'rgba(var(--sky-rgb),0.06)'}}>
                              <div style={{flex:1}}>
                                <div style={{display:'flex',alignItems:'center',gap:8}}>
                                  <span style={{fontWeight:700,fontSize:14,color: cobro.anulado ? 'var(--silver)' : '#22c55e', textDecoration: cobro.anulado ? 'line-through' : 'none'}}>{fmt$(cobro.monto)}</span>
                                  {cobro.anulado && <span style={{fontSize:11,fontWeight:700,color:'var(--text-danger)',background:'rgba(239, 68, 68, 0.15)',padding:'2px 8px',borderRadius:4}}>ANULADO</span>}
                                </div>
                                <div style={{fontSize:12,color:'var(--silver)',marginTop:2}}>
                                  {fmtFecha(cobro.fecha)} · {etiquetaDondePago(cobro.donde_pago)||'—'} · {cobro.forma_pago||'—'}
                                  {cobro.num_recibo_aseg && <span style={{marginLeft:8,color:'var(--sky-light)'}}>Rec. Aseg: {cobro.num_recibo_aseg}</span>}
                                  {cobro.num_recibo_ru && <span style={{marginLeft:8,color:'var(--silver)',fontFamily:'monospace',fontSize:11}}>{cobro.num_recibo_ru}</span>}
                                </div>
                                {cobro.anulado && cobro.motivo_anulacion && (
                                  <div style={{fontSize:11,color:'var(--text-danger)',marginTop:4}}>
                                    Motivo: {cobro.motivo_anulacion}
                                    {cobro.anulado_por && <span style={{color:'var(--silver)',marginLeft:8}}>— {cobro.anulado_por} · {fmtFecha(cobro.anulado_en)}</span>}
                                  </div>
                                )}
                              </div>
                              {/* Reimprimir: el recibo antes solo salía al registrar el pago; si se cerraba
                                  esa ventana no había forma de recuperarlo. Cualquiera que vea el cobro
                                  puede reimprimirlo (no mueve nada); la copia sale marcada REIMPRESIÓN. */}
                              {!cobro.anulado && cobro.donde_pago === 'RU' && cobro.num_recibo_ru && (
                                <button className="btn btn-sm btn-secondary" title="Imprimir de nuevo el recibo de este pago en oficina"
                                  onClick={() => imprimirReciboCobro({
                                    numRecibo: cobro.num_recibo_ru,
                                    fecha: cobro.fecha,
                                    monto: cobro.monto,
                                    formaPago: cobro.forma_pago || null,
                                    cliente: detalle.poliza.cliente,
                                    polizaNum: detalle.poliza.numero,
                                    aseguradora: detalle.poliza.aseguradora,
                                    ramo: detalle.poliza.ramo,
                                    cuotas: (cobro.cobro_cuotas || []).map(cc => ({
                                      num: cc.pagos?.cuota_num,
                                      monto: cc.monto_aplicado,
                                      parcial: Number(cc.monto_aplicado) < Number(cc.pagos?.monto || 0) - 0.005,
                                    })),
                                    reimpresion: true,
                                  })}>
                                  <Printer size={12}/> Reimprimir recibo
                                </button>
                              )}
                              {!cobro.anulado && user?.es_master && (
                                <div style={{display:'flex',gap:6}}>
                                  <button className="btn btn-sm btn-secondary" title="Editar metadata del cobro"
                                    onClick={() => { setModalEditCobro(cobro); setEditCobroForm({ monto: String(cobro.monto ?? ''), motivo_monto: '', fecha: cobro.fecha, donde_pago: cobro.donde_pago||'', forma_pago: cobro.forma_pago||'', num_recibo_aseg: cobro.num_recibo_aseg||'', notas: cobro.notas||'' }); }}>
                                    <Edit2 size={12}/> Editar
                                  </button>
                                  <button className="btn btn-sm btn-secondary" style={{color:'var(--text-danger)',borderColor:'rgba(239, 68, 68, 0.4)'}} title="Anular este cobro"
                                    onClick={() => { setModalAnularCobro(cobro); setMotivoAnulacion(''); }}>
                                    <Trash2 size={12}/> Anular
                                  </button>
                                </div>
                              )}
                            </div>
                            {cobro.cobro_cuotas?.length > 0 && (
                              <div style={{padding:'8px 16px'}}>
                                <div style={{fontSize:11,color:'var(--silver)',marginBottom:6,fontWeight:600,letterSpacing:1}}>CUOTAS CUBIERTAS</div>
                                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                                  {cobro.cobro_cuotas.map((cc,j)=>(
                                    <div key={j} style={{padding:'4px 10px',background:'rgba(var(--steel-rgb),0.4)',borderRadius:6,fontSize:12}}>
                                      <span style={{color:'var(--silver)'}}>Cuota #{cc.pagos?.cuota_num}</span>
                                      <span style={{marginLeft:6,fontWeight:600,color: cobro.anulado ? 'var(--silver)' : cc.monto_aplicado>=cc.pagos?.monto?'#22c55e':'#f59e0b'}}>
                                        {fmt$(cc.monto_aplicado)}
                                        {cc.monto_aplicado < (cc.pagos?.monto||0) && <span style={{fontSize:10,marginLeft:4,color:'var(--text-warning)'}}>(parcial)</span>}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {cobro.notas && !cobro.anulado && (
                              <div style={{padding:'6px 16px 10px',fontSize:12,color:'var(--silver)'}}>{cobro.notas}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Tabla vigencia actual — una fila por cuota */}
                <div style={{ fontSize:11, fontWeight:700, color:'var(--sky-light)', letterSpacing:1, marginBottom:8 }}>VIGENCIA ACTUAL · {fmtFecha(poliza.fecha_inicio)} → {fmtFecha(poliza.fecha_vencimiento)}</div>
                {(() => {
                  const cuotaMap = {};
                  for (const cobro of cobrosRecibidos) {
                    if (cobro.anulado) continue;
                    for (const cc of (cobro.cobro_cuotas || [])) {
                      const cuota = cuotas.find(c => c.id === cc.pagos?.id || c.id === cc.pago_id);
                      if (!cuota) continue;
                      const aplicado = parseFloat(cc.monto_aplicado) || 0;
                      if (!cuotaMap[cuota.id]) {
                        cuotaMap[cuota.id] = {
                          cuota,
                          montoAplicado: 0,
                          fecha: cobro.fecha,
                          recibo: cobro.num_recibo_ru || cobro.num_recibo_aseg || '—',
                          formaPago: cobro.forma_pago || etiquetaDondePago(cobro.donde_pago) || '—',
                        };
                      }
                      cuotaMap[cuota.id].montoAplicado = Math.round(
                        (cuotaMap[cuota.id].montoAplicado + aplicado) * 100
                      ) / 100;
                      if (cobro.fecha > cuotaMap[cuota.id].fecha) {
                        cuotaMap[cuota.id].fecha     = cobro.fecha;
                        cuotaMap[cuota.id].recibo    = cobro.num_recibo_ru || cobro.num_recibo_aseg || '—';
                        cuotaMap[cuota.id].formaPago = cobro.forma_pago || etiquetaDondePago(cobro.donde_pago) || '—';
                      }
                    }
                  }

                  const filas = [];
                  const cuotasConTx = new Set(Object.keys(cuotaMap).map(Number));
                  for (const fila of Object.values(cuotaMap)) filas.push(fila);

                  for (const c of cuotas.filter(c => !cuotasConTx.has(c.id))) {
                    const esPagada = c.estado === 'pagado';
                    filas.push({
                      cuota: c,
                      montoAplicado: esPagada ? (parseFloat(c.monto_pagado) || parseFloat(c.monto) || null) : null,
                      fecha:      esPagada ? (c.fecha_pago || null)     : null,
                      recibo:     esPagada ? (c.num_recibo_ru || c.num_recibo_aseg || null) : null,
                      formaPago:  esPagada ? (c.forma_pago_real || etiquetaDondePago(c.donde_pago) || null)    : null,
                    });
                  }
                  filas.sort((a,b) => (a.cuota.cuota_num||0) - (b.cuota.cuota_num||0));

                  return (
                  <div className="table-wrapper" style={{ marginBottom:16 }}>
                    <table>
                      <thead><tr>
                        <th>Cuota</th><th>Vencimiento</th><th>Total cuota</th>
                        <th>Este pago</th><th>Estado</th>
                        <th>Fecha pago</th><th>Forma</th><th>Recibo</th>
                      </tr></thead>
                      <tbody>
                        {filas.length === 0
                          ? <tr><td colSpan={8} style={{textAlign:'center',color:'var(--silver)',padding:20}}>Sin cuotas registradas</td></tr>
                          : filas.map((f, i) => {
                            const c = f.cuota;
                            const venc = ['pendiente','parcial'].includes(c.estado) && c.fecha_vencimiento && c.fecha_vencimiento < hoyStr;
                            const badgeColor = c.estado==='pagado' ? 'badge-success'
                              : c.estado==='parcial' ? 'badge-warning'
                              : venc ? 'badge-danger' : 'badge-warning';
                            const labelEstado = c.estado==='parcial'
                              ? `Parcial ${Math.round((parseFloat(c.monto_pagado)||0)/(parseFloat(c.monto)||1)*100)}%`
                              : c.estado;
                            const rowBg = c.estado==='pagado' ? {}
                              : c.estado==='parcial' ? {background:'rgba(245, 158, 11, 0.04)'}
                              : venc ? {background:'rgba(239, 68, 68, 0.04)'} : {};
                            const prevCuotaId = i > 0 ? filas[i-1].cuota.id : null;
                            const esPrimeraDeCuota = c.id !== prevCuotaId;
                            const esPagada = c.estado === 'pagado' || (f.montoAplicado != null);
                            return (
                              <tr key={`${c.id}-${i}`}
                                onClick={() => esPagada && setPagoDetalle({ cuota: c, fila: f })}
                                style={{
                                  ...rowBg,
                                  borderTop: esPrimeraDeCuota && i>0 ? '2px solid rgba(var(--sky-rgb),0.15)' : undefined,
                                  cursor: esPagada ? 'pointer' : 'default',
                                }}
                                title={esPagada ? 'Ver detalle del cobro' : ''}
                              >
                                <td style={{color:'var(--silver)',fontSize:12,fontWeight: esPrimeraDeCuota?700:400}}>
                                  #{c.cuota_num||'—'}
                                </td>
                                <td style={{fontSize:12,color:venc?'#ef4444':'inherit'}}>
                                  {fmtFecha(c.fecha_vencimiento)||'—'}{venc&&<AlertTriangle size={11} style={{marginLeft:4,display:'inline'}}/>}
                                </td>
                                <td style={{fontWeight:600}}>{fmt$(c.monto)}</td>
                                <td style={{
                                  color: f.montoAplicado!=null ? '#22c55e' : 'var(--silver)',
                                  fontWeight: f.montoAplicado!=null ? 600 : 400,
                                }}>
                                  {f.montoAplicado!=null ? fmt$(f.montoAplicado) : '—'}
                                </td>
                                <td><span className={`badge ${badgeColor}`} style={{fontSize:10}}>{labelEstado}</span></td>
                                <td style={{fontSize:12,color:'var(--silver)'}}>{fmtFecha(f.fecha)||'—'}</td>
                                <td style={{fontSize:12,color:'var(--silver)'}}>{f.formaPago||'—'}</td>
                                <td style={{fontSize:11,color:'var(--sky-light)',fontFamily:'monospace'}}>{f.recibo||'—'}</td>
                              </tr>
                            );
                          })
                        }
                      </tbody>
                    </table>
                  </div>
                  );
                })()}

                {/* Tabla vigencia anterior — solo si tiene saldo pendiente */}
                {hayAnteriorPendiente && (() => {
                  const antPendiente = cuotasAnteriores.filter(c=>['pendiente','parcial'].includes(c.estado)).reduce((s,c)=>s+Math.max(0,(parseFloat(c.monto)||0)-(parseFloat(c.monto_pagado)||0)),0);
                  const antPagado    = cuotasAnteriores.filter(c=>c.estado==='pagado').reduce((s,c)=>s+(parseFloat(c.monto)||0),0)+(cuotasAnteriores.filter(c=>c.estado==='parcial').reduce((s,c)=>s+(parseFloat(c.monto_pagado)||0),0));
                  return (
                    <div style={{ borderRadius:10, border:'1px solid rgba(245, 158, 11, 0.3)', overflow:'hidden', marginBottom:16 }}>
                      <div style={{ padding:'10px 14px', background:'rgba(245, 158, 11, 0.07)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div>
                          <span style={{ fontSize:11, fontWeight:700, color:'var(--text-warning)', letterSpacing:1 }}>VIGENCIA ANTERIOR</span>
                          <span style={{ fontSize:12, color:'var(--silver)', marginLeft:10 }}>
                            {poliza.poliza_anterior?.numero} · {fmtFecha(poliza.poliza_anterior?.fecha_inicio)} → {fmtFecha(poliza.poliza_anterior?.fecha_vencimiento)}
                          </span>
                        </div>
                        <div style={{ display:'flex', gap:16, fontSize:13 }}>
                          <span style={{ color:'var(--text-success)' }}>Pagado: <strong>{fmt$(antPagado)}</strong></span>
                          <span style={{ color: antPendiente>0?'#ef4444':'#22c55e' }}>Saldo: <strong>{fmt$(antPendiente)}</strong></span>
                        </div>
                      </div>
                      <div className="table-wrapper" style={{ margin:0 }}>
                        <table>
                          <thead><tr><th>Cuota</th><th>Vencimiento</th><th>Monto</th><th>Estado</th><th>Fecha Pago</th><th>Recibo</th></tr></thead>
                          <tbody>
                            {cuotasAnteriores.map(c => {
                              const venc = c.estado==='pendiente'&&c.fecha_vencimiento&&c.fecha_vencimiento<hoyStr;
                              return (
                                <tr key={c.id} style={venc?{background:'rgba(239, 68, 68, 0.04)'}:{}}>
                                  <td style={{ color:'var(--silver)', fontSize:12 }}>#{c.cuota_num||'—'}</td>
                                  <td style={{ fontSize:12, color:venc?'#ef4444':'inherit' }}>{fmtFecha(c.fecha_vencimiento)||'—'}{venc&&<AlertTriangle size={11} style={{marginLeft:4,display:'inline'}}/>}</td>
                                  <td style={{ fontWeight:600 }}>{fmt$(c.monto)}</td>
                                  <td><span className={`badge ${c.estado==='pagado'?'badge-success':venc?'badge-danger':'badge-warning'}`}>{c.estado}</span></td>
                                  <td style={{ fontSize:12, color:'var(--silver)' }}>{fmtFecha(c.fecha_pago)||'—'}</td>
                                  <td style={{ fontSize:11, color:'var(--silver)', fontFamily:'monospace' }}>{c.num_recibo_ru||c.num_recibo_aseg||'—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* Plan de pago */}
                {(poliza.frecuencia_pago || poliza.num_cuotas) && (
                  <div style={{ marginTop:0, padding:'10px 14px', background:'rgba(var(--sky-rgb),0.07)', borderRadius:8, display:'flex', gap:24, fontSize:13, marginBottom:16 }}>
                    <div><span style={{ color:'var(--silver)', fontSize:11, textTransform:'uppercase' }}>Frecuencia </span><strong>{poliza.frecuencia_pago || '—'}</strong></div>
                    <div><span style={{ color:'var(--silver)', fontSize:11, textTransform:'uppercase' }}>Cuotas </span><strong>{poliza.num_cuotas || '—'}</strong></div>
                    {poliza.prima && poliza.num_cuotas > 1 && (
                      <div><span style={{ color:'var(--silver)', fontSize:11, textTransform:'uppercase' }}>Monto por cuota </span>
                        <strong>{fmt$(Math.round(poliza.prima * (1 + (poliza.impuesto==null?5:(parseFloat(poliza.impuesto)||0))/100) / poliza.num_cuotas * 100)/100)}</strong>
                      </div>
                    )}
                  </div>
                )}

                {/* Bitácora de cobro */}
                <div style={{ marginTop:4 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:10 }}>BITÁCORA DE COBRO</div>
                  <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                    <input className="form-input" placeholder="Agregar nota de gestión de cobro..." value={notaCobro}
                      onChange={e=>setNotaCobro(e.target.value)}
                      onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&agregarNotaCobro()}
                      style={{ flex:1 }} />
                    <button className="btn btn-primary btn-sm" onClick={agregarNotaCobro} disabled={guardandoNota || !notaCobro.trim()}>
                      {guardandoNota ? '...' : 'Agregar'}
                    </button>
                  </div>
                  <div style={{ maxHeight:200, overflowY:'auto' }}>
                    {bitacoraCobro.length === 0
                      ? <div style={{ fontSize:12, color:'var(--silver)' }}>Sin notas de cobro todavía.</div>
                      : bitacoraCobro.map((b, i) => (
                        <div key={i} style={{ display:'flex', gap:12, alignItems:'baseline', padding:'5px 4px', borderBottom:'1px solid rgba(var(--silver-rgb),0.1)', fontSize:12 }}>
                          <span style={{ flex:1 }}>{b.nota}</span>
                          <span style={{ color:'var(--silver)', fontSize:11, whiteSpace:'nowrap' }}>
                            {b.usuarios?.nombre || 'Sistema'} · {fmtFechaHora(b.creado_en)}
                          </span>
                        </div>
                      ))
                    }
                  </div>
                </div>
                </>);
                })()}

              </div>

              <div className="modal-footer">
                {subVista === 'poliza'
                  ? <button className="btn btn-secondary" onClick={()=>setSubVista('cuotas')}>← Volver</button>
                  : <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
                }
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal Editar Cobro ── */}
      {modalEditCobro && (
        <div className="modal-overlay" onClick={() => setModalEditCobro(null)}>
          <div className="modal" style={{ maxWidth:480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Editar Cobro</div>
                <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>{fmt$(modalEditCobro.monto)} · {modalEditCobro.num_recibo_ru || modalEditCobro.num_recibo_aseg || '—'}</div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={() => setModalEditCobro(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              {(() => {
                const cambia = Math.abs((parseFloat(editCobroForm.monto) || 0) - (parseFloat(modalEditCobro.monto) || 0)) > 0.005;
                return (
                  <>
                    <div style={{ padding:'8px 12px', background:'rgba(245, 158, 11, 0.08)', borderRadius:8, marginBottom:16, fontSize:12, color:'var(--text-warning)' }}>
                      {cambia
                        ? <>Vas a corregir el <strong>monto</strong> de {fmt$(modalEditCobro.monto)} a {fmt$(editCobroForm.monto)}. Se reaplican las cuotas y se recalcula la comisión: si nadie la ha pagado se regenera con el monto correcto; si ya se pagó o está en proceso, no se reescribe y se genera un ajuste por la diferencia.</>
                        : <>Si el monto se registró mal, corrígelo aquí: las cuotas y la comisión se recalculan solas. Fecha, recibo y forma de pago se editan sin mover dinero.</>}
                    </div>
                    <div className="form-grid form-grid-2">
                      <div className="form-group">
                        <label className="form-label">Monto del cobro *</label>
                        <input className="form-input" type="number" step="0.01" min="0.01" value={editCobroForm.monto ?? ''}
                          disabled={!!modalEditCobro.remesa_id}
                          title={modalEditCobro.remesa_id ? 'Este cobro ya va en una remesa: su monto no se corrige aquí' : ''}
                          onChange={e => setEditCobroForm(f=>({...f, monto:e.target.value}))}/>
                      </div>
                      {cambia && (
                        <div className="form-group">
                          <label className="form-label">Motivo de la corrección *</label>
                          <input className="form-input" value={editCobroForm.motivo_monto||''} onChange={e => setEditCobroForm(f=>({...f, motivo_monto:e.target.value}))} placeholder="Ej: se digitó 105 y el recibo dice 95"/>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Fecha del cobro *</label>
                  <input className="form-input" type="date" value={editCobroForm.fecha||''} onChange={e => setEditCobroForm(f=>({...f, fecha:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">¿Dónde pagó?</label>
                  <select className="form-select" value={editCobroForm.donde_pago||''} onChange={e => setEditCobroForm(f=>({...f, donde_pago:e.target.value}))}>
                    <option value="Aseguradora">Aseguradora</option>
                    <option value="RU">Oficina</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Forma de pago</label>
                  <input className="form-input" value={editCobroForm.forma_pago||''} onChange={e => setEditCobroForm(f=>({...f, forma_pago:e.target.value}))} placeholder="Ej: Transferencia, ACH"/>
                </div>
                <div className="form-group">
                  <label className="form-label">No. Recibo Aseg.</label>
                  <input className="form-input" value={editCobroForm.num_recibo_aseg||''} onChange={e => setEditCobroForm(f=>({...f, num_recibo_aseg:e.target.value}))} placeholder="Opcional"/>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notas</label>
                <textarea className="form-input" rows={2} value={editCobroForm.notas||''} onChange={e => setEditCobroForm(f=>({...f, notas:e.target.value}))} style={{ resize:'vertical', fontFamily:'inherit' }}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalEditCobro(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleEditarCobro} disabled={procesandoCobro || !editCobroForm.fecha}>
                <Check size={14}/> {procesandoCobro ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Anular Cobro ── */}
      {modalAnularCobro && (
        <div className="modal-overlay" onClick={() => setModalAnularCobro(null)}>
          <div className="modal" style={{ maxWidth:460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title" style={{ color:'var(--text-danger)' }}>Anular Cobro</div>
                <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>{fmt$(modalAnularCobro.monto)} · {fmtFecha(modalAnularCobro.fecha)}</div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={() => setModalAnularCobro(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{ padding:'12px 14px', background:'rgba(239, 68, 68, 0.08)', border:'1px solid rgba(239, 68, 68, 0.25)', borderRadius:8, marginBottom:16, fontSize:13, color:'var(--text-danger)' }}>
                <strong>Esta acción:</strong>
                <ul style={{ margin:'6px 0 0', paddingLeft:18, lineHeight:1.9, fontSize:12 }}>
                  <li>Marca el cobro como ANULADO (queda visible en el historial)</li>
                  <li>Revierte las cuotas cubiertas a pendiente</li>
                  <li>Elimina la comisión de este cobro si aún nadie la ha pagado</li>
                  <li>Si la comisión ya se pagó o está en proceso, NO se borra: se genera un ajuste en negativo que se descuenta en la próxima liquidación</li>
                </ul>
              </div>
              <div style={{ marginBottom:16, fontSize:13, color:'var(--silver)' }}>
                Cuotas que se revertirán: <strong style={{ color:'var(--text)' }}>
                  {(modalAnularCobro.cobro_cuotas||[]).map(cc=>`#${cc.pagos?.cuota_num}`).join(', ') || '—'}
                </strong>
              </div>
              <div className="form-group">
                <label className="form-label">Motivo de anulación *</label>
                <textarea className="form-input" rows={3} value={motivoAnulacion} onChange={e => setMotivoAnulacion(e.target.value)}
                  placeholder="Describe por qué se anula este cobro (ej: pago duplicado, error de monto, etc.)"
                  style={{ resize:'vertical', fontFamily:'inherit' }}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalAnularCobro(null)}>Cancelar</button>
              <button className="btn btn-secondary" style={{ color:'var(--text-danger)', borderColor:'rgba(239, 68, 68, 0.4)' }}
                onClick={handleAnularCobro} disabled={procesandoCobro || !motivoAnulacion.trim()}>
                <Trash2 size={14}/> {procesandoCobro ? 'Anulando...' : 'Confirmar anulación'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Detalle de Pago ── */}
      {pagoDetalle && (() => {
        const { cuota: c, fila: f } = pagoDetalle;
        const filaDetalle = [
          ['Cuota',              `#${c.cuota_num || '—'}`],
          ['Fecha de vencimiento', fmtFecha(c.fecha_vencimiento) || '—'],
          ['Total cuota',        fmt$(c.monto)],
          ['Monto aplicado',     f.montoAplicado != null ? fmt$(f.montoAplicado) : '—'],
          ['Monto pagado acum.', c.monto_pagado ? fmt$(c.monto_pagado) : null],
          ['Estado',             c.estado],
          ['Fecha de pago',      fmtFecha(f.fecha || c.fecha_pago) || '—'],
          ['Dónde pagó',         etiquetaDondePago(c.donde_pago) || '—'],
          ['Forma de pago',      f.formaPago || c.forma_pago_real || '—'],
          ['No. Recibo Aseg.',   c.num_recibo_aseg || '—'],
          [`No. Recibo ${abrev}`,      f.recibo || c.num_recibo_ru || '—'],
          ['Notas',              c.notas || null],
        ];
        return (
          <div className="modal-overlay" onClick={() => setPagoDetalle(null)}>
            <div className="modal" style={{ maxWidth:420 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <div className="modal-title">Detalle del Cobro</div>
                  <div className="text-silver text-sm">Cuota #{c.cuota_num || '—'} · {fmtFecha(c.fecha_vencimiento)}</div>
                </div>
                <button className="btn btn-icon btn-secondary btn-sm" onClick={() => setPagoDetalle(null)}><X size={16}/></button>
              </div>
              <div className="modal-body">
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {filaDetalle.filter(([,v]) => v != null).map(([label, valor]) => (
                    <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'8px 12px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8 }}>
                      <span style={{ fontSize:12, color:'var(--silver)', textTransform:'uppercase', letterSpacing:0.5 }}>{label}</span>
                      <span style={{ fontSize:13, fontWeight:600, fontFamily: label.includes('Recibo') ? 'monospace' : 'inherit', color: label==='Monto aplicado'||label==='Total cuota' ? '#22c55e' : 'inherit' }}>
                        {valor}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setPagoDetalle(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal éxito de pago en oficina: recibo asignado + imprimir ── */}
      {reciboExito && <ReciboCobroExito datos={reciboExito} onClose={() => setReciboExito(null)} />}
    </>
  );
}
